import { authFetch } from './authFetch';

const FILTER_PRESETS_CACHE_PREFIX = 'filterPresets_';
const CACHE_TTL = 7 * 24 * 60 * 60 * 1000;

const inflightHydration = new Map();

/** True when saved presets contain at least one filter value or min/max range. */
export const filterPresetsHaveContent = (filters, minMaxFilters) => {
  const f = filters || {};
  const mm = minMaxFilters || {};
  const hasFilterValues = Object.values(f).some((v) => v != null && v !== '');
  const hasMinMax = Object.values(mm).some((range) => {
    if (!range || typeof range !== 'object') return false;
    return (range.min != null && range.min !== '') || (range.max != null && range.max !== '');
  });
  return hasFilterValues || hasMinMax;
};

export const getFilterPresetsCacheKey = (userEmail) => {
  if (!userEmail) return null;
  return `${FILTER_PRESETS_CACHE_PREFIX}${userEmail}`;
};

/**
 * Read cached filter presets for a user (client-side read cache).
 * @returns {{ filters: object, minMaxFilters: object, timestamp: number } | null}
 */
export const getCachedFilterPresets = (userEmail) => {
  const key = getFilterPresetsCacheKey(userEmail);
  if (!key) return null;

  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.timestamp !== 'number') {
      localStorage.removeItem(key);
      return null;
    }

    const age = Date.now() - parsed.timestamp;
    if (age >= CACHE_TTL) {
      localStorage.removeItem(key);
      return null;
    }

    return {
      filters: parsed.filters || {},
      minMaxFilters: parsed.minMaxFilters || {},
      timestamp: parsed.timestamp,
    };
  } catch (error) {
    console.warn(`[FILTER PRESETS] Error reading cache for ${userEmail}:`, error);
    try {
      localStorage.removeItem(key);
    } catch { /* ignore */ }
    return null;
  }
};

export const setCachedFilterPresets = (userEmail, { filters, minMaxFilters, timestamp }) => {
  const key = getFilterPresetsCacheKey(userEmail);
  if (!key) return;

  try {
    localStorage.setItem(key, JSON.stringify({
      filters: filters || {},
      minMaxFilters: minMaxFilters || {},
      timestamp: typeof timestamp === 'number' ? timestamp : Date.now(),
    }));
  } catch (error) {
    console.warn(`[FILTER PRESETS] Error caching presets for ${userEmail}:`, error);
  }
};

export const clearCachedFilterPresets = (userEmail) => {
  const key = getFilterPresetsCacheKey(userEmail);
  if (!key) return;

  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.warn(`[FILTER PRESETS] Error clearing cache for ${userEmail}:`, error);
  }
};

/**
 * Hydrate local cache from DynamoDB when missing (e.g. after reload or new device).
 * Returns cached or freshly fetched presets, or null if user has no saved defaults.
 */
export const hydrateFilterPresets = async (userEmail) => {
  if (!userEmail) return null;

  const cached = getCachedFilterPresets(userEmail);
  if (cached) return cached;

  if (inflightHydration.has(userEmail)) {
    return inflightHydration.get(userEmail);
  }

  const promise = (async () => {
    try {
      const response = await authFetch(`/api/filter-presets/${userEmail}`);
      if (!response.ok) return null;

      const data = await response.json();
      const filters = data.filterPresets || {};
      const minMaxFilters = data.minMaxFilters || {};

      if (!filterPresetsHaveContent(filters, minMaxFilters)) {
        return null;
      }

      const presets = {
        filters,
        minMaxFilters,
        timestamp: typeof data.timestamp === 'number' ? data.timestamp : Date.now(),
      };
      setCachedFilterPresets(userEmail, presets);
      return presets;
    } catch (error) {
      console.error('[FILTER PRESETS] Error hydrating from server:', error);
      return null;
    } finally {
      inflightHydration.delete(userEmail);
    }
  })();

  inflightHydration.set(userEmail, promise);
  return promise;
};

/** Clear local preset cache and re-fetch from the server (e.g. screener refresh). */
export const refreshFilterPresetsFromServer = async (userEmail) => {
  if (!userEmail) return null;
  clearCachedFilterPresets(userEmail);
  inflightHydration.delete(userEmail);
  return hydrateFilterPresets(userEmail);
};
